import { useState, useEffect } from 'react';
import { FileText, File, Image as ImageIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useFS } from '@/hooks/useFS';
import type { JSRuntimeFS } from '@/lib/JSRuntime';
import type OpenAI from 'openai';

interface UserMessageProps {
  content: string | Array<OpenAI.Chat.Completions.ChatCompletionContentPartText>;
}

// Helper functions outside component to prevent recreating on each render
const getFileIcon = (filename: string) => {
  const extension = filename.split('.').pop()?.toLowerCase();

  // Image extensions
  const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'];
  if (extension && imageExtensions.includes(extension)) {
    return <ImageIcon className="h-3 w-3" />;
  }

  // Return FileText icon for text-like files, File icon for others
  const textExtensions = ['txt', 'md', 'json', 'js', 'ts', 'jsx', 'tsx', 'css', 'html', 'xml', 'yml', 'yaml'];

  if (extension && textExtensions.includes(extension)) {
    return <FileText className="h-3 w-3" />;
  }

  return <File className="h-3 w-3" />;
};

const getFileName = (filepath: string) => {
  return filepath.split('/').pop() || filepath;
};

const isImageFile = (filename: string) => {
  const extension = filename.split('.').pop()?.toLowerCase();
  const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'];
  return extension && imageExtensions.includes(extension);
};

// Component to render image preview
function ImagePreview({ filepath, fs }: { filepath: string; fs: JSRuntimeFS }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // Load image on mount
  useEffect(() => {
    let url: string | null = null;

    const loadImage = async () => {
      try {
        const data = await fs.readFile(filepath);
        const blob = new Blob([data]);
        url = URL.createObjectURL(blob);
        setImageUrl(url);
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to load image:', error);
        setError(true);
        setIsLoading(false);
      }
    };

    loadImage();

    // Cleanup: revoke object URL when component unmounts
    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [filepath, fs]);

  const filename = getFileName(filepath);

  if (error) {
    // Fallback to badge if image fails to load
    return (
      <Badge variant="secondary" className="flex items-center gap-1.5 px-2 py-1">
        <ImageIcon className="h-3 w-3" />
        <span className="text-xs font-medium">{filename}</span>
      </Badge>
    );
  }

  return (
    <div>
      {isLoading ? (
        <div className="h-32 w-32 bg-muted animate-pulse rounded-lg" />
      ) : (
        <img
          src={imageUrl || ''}
          alt={filename}
          className="max-w-full max-h-64 rounded-lg border border-border shadow-sm object-contain bg-muted"
        />
      )}
    </div>
  );
}

/**
 * Component to render user messages nicely
 * Handles both string content and array content (text parts)
 * Detects "Added file: /tmp/<filename>" patterns and renders them as badges or image previews
 */
export function UserMessage({ content }: UserMessageProps) {
  const { fs } = useFS();

  // Convert content to array format for uniform processing
  const contentParts = typeof content === 'string'
    ? [{ type: 'text' as const, text: content }]
    : content.filter(part => part.type === 'text') as Array<{ type: 'text'; text: string }>;

  // Regular expression to match "Added file: /tmp/<filename>" patterns
  const filePattern = /^Added file: (\/tmp\/[^\s\n]+)$/;

  // Process each content part and categorize as text or file
  const processedParts: Array<{ type: 'text' | 'file'; content: string; filepath?: string }> = [];

  contentParts.forEach(part => {
    const text = part.text.trim();
    const match = text.match(filePattern);

    if (match) {
      // This is a file attachment
      const filepath = match[1]; // "/tmp/filename.txt"
      processedParts.push({ type: 'file', content: text, filepath });
    } else if (text) {
      // This is regular text content
      processedParts.push({ type: 'text', content: text });
    }
  });

  // If no content, return empty
  if (processedParts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {processedParts.map((part, index) => {
        if (part.type === 'text') {
          return (
            <div key={index} className="whitespace-pre-wrap">
              {part.content}
            </div>
          );
        } else {
          const filename = getFileName(part.filepath!);

          // Render image preview for image files
          if (isImageFile(filename)) {
            return (
              <div key={index}>
                <ImagePreview filepath={part.filepath!} fs={fs} />
              </div>
            );
          }

          // Render badge for non-image files
          return (
            <div key={index} className="flex items-center gap-2">
              <Badge variant="secondary" className="flex items-center gap-1.5 px-2 py-1">
                {getFileIcon(filename)}
                <span className="text-xs font-medium">{filename}</span>
              </Badge>
            </div>
          );
        }
      })}
    </div>
  );
}